/**
 * ErrorBoundary Component
 *
 * Catches React errors and displays a user-friendly fallback UI.
 * Automatically reports errors to Sentry for tracking.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <YourApp />
 * </ErrorBoundary>
 * ```
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
// Temporarily use simple fallback to avoid ErrorBoundary self-failure
// import { Alert } from './ui/Alert';
// import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console for debugging
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);

    // Send to Sentry with React component stack
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorType: 'react_error_boundary',
      },
      level: 'error',
    });

    // Update state with error details
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    // Clear error state and reload page
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error and navigate to home
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI - Uses design tokens via CSS classes
      // Simple structure that works even if component library fails
      return (
        <div
          className="error-boundary-container"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div
            className="error-boundary-card"
            role="alertdialog"
            aria-labelledby="error-boundary-title"
            aria-describedby="error-boundary-message"
          >
            <h1
              id="error-boundary-title"
              className="error-boundary-title"
            >
              Something went wrong
            </h1>
            <p
              id="error-boundary-message"
              className="error-boundary-message"
            >
              We're sorry, but something unexpected happened. Our team has been notified and we're working on a fix.
            </p>

            {/* Action Buttons */}
            <div className="error-boundary-actions">
              <button
                onClick={this.handleReload}
                className="error-boundary-button-secondary"
                aria-label="Reload the current page to try again"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="error-boundary-button-primary"
                aria-label="Go to home page"
              >
                Go to Home
              </button>
            </div>

            {/* Error Details (Development only) */}
            {import.meta.env.DEV && this.state.error && (
              <div className="error-boundary-details">
                <h2 className="error-boundary-details-title">
                  Error Details (dev only)
                </h2>
                <div style={{ marginBottom: '16px' }}>
                  <p className="error-boundary-details-label">
                    Error:
                  </p>
                  <pre className="error-boundary-details-pre">
                    {this.state.error.toString()}
                  </pre>
                </div>

                {this.state.errorInfo?.componentStack && (
                  <div>
                    <p className="error-boundary-details-label">
                      Component Stack:
                    </p>
                    <pre className="error-boundary-details-pre" style={{ maxHeight: '200px' }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Support Info */}
            <div className="error-boundary-support">
              <p>If this problem persists, please contact support:</p>
              <a href="mailto:support@etchare.com">
                support@etchare.com
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap a component with ErrorBoundary
 *
 * @example
 * ```tsx
 * const SafeComponent = withErrorBoundary(MyComponent);
 * ```
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
